import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteLink,
    fetchEmployeeOnboardingProfiles,
    fetchMyLinks,
    resendLink,
} from '../../../adminSlice';
import { ONBOARDING_FINAL_STEP } from '../../../../config';
import ProfileModal from './profileModal';
import Tooltip from './tooltip';

/** Ids and branch names come back with inconsistent casing, so keys are compared lower-cased. */
const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

/**
 * The branch is written on the link when it is generated, but the flows without a
 * Branch dropdown leave it empty and older links never carried one — in both cases
 * the onboarding profile still holds it under `branch_location`.
 */
const branchOf = (link, profile) => {
    const candidates = [
        link?.data?.branch,
        link?.data?.branch_location?.branch,
        profile?.profile?.data?.branch_location?.branch,
        profile?.profile?.data?.branch_location?._branch?.BranchNickname,
    ];
    return candidates.map(value => String(value ?? '').trim()).find(Boolean) || '';
};

const formatDate = (value) => new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
}).format(new Date(value));

function MyLinks() {
    const links = useSelector(state => state.admin.data);
    const profiles = useSelector(state => state.admin.profiles);
    const token = useSelector(state => state.admin.token);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalProfile, setModalProfile] = useState();
    const dispatch = useDispatch();

    // The profiles are keyed by user id, and the link and the profile endpoints do
    // not always return that id with the same casing — index it lower-cased so a
    // profile is still found, instead of the row falling back to 0% and no branch.
    const profilesByUserId = useMemo(() => {
        const index = {};
        Object.entries(profiles || {}).forEach(([userId, profile]) => {
            index[normalizeKey(userId)] = profile;
        });
        return index;
    }, [profiles]);

    const profileOf = (userId) => profilesByUserId[normalizeKey(userId)];

    // Lazy fetch — only fires when My Links actually opens. Previously eager in AdminHome.
    useEffect(() => {
        if (!token) return;
        dispatch(fetchMyLinks());
        dispatch(fetchEmployeeOnboardingProfiles());
    }, [dispatch, token]);

    const getProgressPercentage = (currentStep) => {
        const step = Number(currentStep);

        // Missing, invalid or not-yet-started steps mean no progress has been made
        if (!Number.isFinite(step) || step <= 1) {
            return '0%';
        }
        else {
            return Math.min(step / ONBOARDING_FINAL_STEP * 100, 100).toFixed(0) + '%'
        }
    }

    const handleModalClose = () => {
        setIsModalOpen(false);
    }

    const handleView = (profile) => {
        setModalProfile(profile);
        setIsModalOpen(true)
    }

    const handleDeleteLink = (data) => {
        dispatch(deleteLink(data))
    }

    const handleResendLink = (data) => {
        dispatch(resendLink(data))
    }

    const linkRows = Array.isArray(links) ? links : [];

    // `getProgressPercentage` already returns a CSS-ready string such as "42%", so
    // the same value drives the label and the bar width.
    const renderProgress = (profile) => {
        const progress = getProgressPercentage(profile?.entry_point?.current_step);
        return (
            <div className="min-w-[88px]">
                <span className="text-sm font-medium text-blue-700">{progress}</span>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all" style={{ width: progress }} />
                </div>
            </div>
        );
    };

    const renderRecipientLink = (link) => (
        <Tooltip
            text={"On clicking the link, you will be able to assist the Receipient with the form"}
            className="w-64"
        >
            <a className='font-semibold text-[#fb6635] underline decoration-orange-200 underline-offset-2 hover:decoration-[#fb6635]' target='_blank' href={`${window.location.origin}?token=${link.token}`} rel="noopener noreferrer">
                {link.data.recipientName}
            </a>
        </Tooltip>
    );

    const iconButton = 'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40';

    const renderActions = (link, profile) => (
        <div className="flex items-center gap-1">
            <Tooltip text={'Copy Link'}>
                <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}?token=${link.token}`)}
                    className={`${iconButton} text-[#211b4f] hover:bg-[#211b4f]/5 focus:ring-[#211b4f]`}
                    title='Copy Link'
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-copy" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
                    </svg>
                </button>
            </Tooltip>
            <Tooltip text={'View Profile'}>
                <button
                    onClick={e => handleView(profile)}
                    className={`${iconButton} text-[#fb6635] hover:bg-orange-50 focus:ring-orange-400`}
                    // disabled={profile?.entry_point?.current_step !== ONBOARDING_FINAL_STEP}
                    title={profile?.entry_point?.current_step !== ONBOARDING_FINAL_STEP ? 'Not allowed until completed' : 'View Profile'}
                >
                    <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeWidth="2" d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"/>
                        <path stroke="currentColor" strokeWidth="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                    </svg>
                </button>
            </Tooltip>
            <Tooltip text={'Resend Link'}>
                <button
                    onClick={() => handleResendLink(link)}
                    className={`${iconButton} text-[#211b4f] hover:bg-[#211b4f]/5 focus:ring-[#211b4f]`}
                    title='Resend Link'
                >
                    <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.651 7.65a7.131 7.131 0 0 0-12.68 3.15M18.001 4v4h-4m-7.652 8.35a7.13 7.13 0 0 0 12.68-3.15M6 20v-4h4"/>
                    </svg>
                </button>
            </Tooltip>
            <Tooltip text={'Delete'}>
                <button
                    onClick={() => handleDeleteLink(link)}
                    className={`${iconButton} text-red-600 hover:bg-red-50 focus:ring-red-400`}
                    disabled={profile?.entry_point?.current_step === ONBOARDING_FINAL_STEP}
                    title={profile?.entry_point?.current_step === ONBOARDING_FINAL_STEP ? 'Not allowed to delete completed profile' : 'Delete'}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                    </svg>
                </button>
            </Tooltip>
        </div>
    );

    return (
        <div className="min-w-0 grow bg-[#f2ece9]">

        {/* Panel body */}
        <div className="space-y-5 p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-[#0a1445] sm:text-3xl">
                        My Links
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        Links generated by you.
                    </p>
                </div>
                {linkRows.length > 0 && (
                    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-[#211b4f] shadow-sm">
                        <span className="h-2 w-2 rounded-full bg-[#fb6635]" />
                        {linkRows.length} links
                    </div>
                )}
            </div>

            {/* Links Table */}
            <section>
                {/* Table — desktop */}
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[1120px] border-separate border-spacing-y-2 text-left text-sm text-[#211b4f]">
                        <thead className="bg-[#211b4f] text-[11px] font-semibold uppercase tracking-wider text-white">
                            <tr>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Created on</th>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Branch</th>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Progress</th>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Recipient Name</th>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Recipient Email</th>
                                <th scope="col" className="border-r border-white/20 px-5 py-3.5">Link Expiration Date</th>
                                <th scope="col" className="px-5 py-3.5 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {linkRows.map((link, index) => {
                                const profile = profileOf(link.userId);
                                const branch = branchOf(link, profile);
                                return (
                                    <tr key={link.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#eadfda]'} transition-colors hover:bg-[#fff7f2]`}>
                                        <td className="whitespace-nowrap px-5 py-4 font-semibold">
                                            {formatDate(link.created_on)}
                                        </td>
                                        <td className="w-[240px] whitespace-normal break-words px-5 py-4 leading-snug" title={branch}>
                                            {branch || '—'}
                                        </td>
                                        <td className="px-5 py-4" title={link.userId}>
                                            {renderProgress(profile)}
                                        </td>
                                        <td className="w-[200px] whitespace-normal break-words px-5 py-4 leading-snug" title={link.data.recipientName}>
                                            {renderRecipientLink(link)}
                                        </td>
                                        <td className="w-[220px] whitespace-normal break-words px-5 py-4 leading-snug text-slate-600" title={link.data.recipientEmail}>
                                            {link.data.recipientEmail}
                                        </td>
                                        <td className="whitespace-nowrap px-5 py-4 font-medium">
                                            {formatDate(link.expiry_time)}
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex justify-end">{renderActions(link, profile)}</div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Cards — mobile */}
                <div className="grid gap-3 md:hidden">
                    {linkRows.map(link => {
                        const profile = profileOf(link.userId);
                        const branch = branchOf(link, profile);
                        return (
                            <article key={link.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Recipient Name</p>
                                        <div className="mt-1 break-words text-base font-bold">{renderRecipientLink(link)}</div>
                                    </div>
                                    <div className="flex-none text-right">
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Created on</p>
                                        <p className="mt-1 text-sm font-semibold text-[#211b4f]">{formatDate(link.created_on)}</p>
                                    </div>
                                </div>

                                <div className="mt-4 border-y border-slate-100 py-3">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Recipient Email</p>
                                    <p className="mt-1 break-all text-sm text-slate-600">{link.data.recipientEmail}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 py-3">
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Branch</p>
                                        <p className="mt-1 break-words text-sm font-medium text-[#211b4f]">{branch || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Progress</p>
                                        <div className="mt-1">{renderProgress(profile)}</div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Link Expiration Date</p>
                                        <p className="mt-1 text-sm font-medium text-[#211b4f]">{formatDate(link.expiry_time)}</p>
                                    </div>
                                    {renderActions(link, profile)}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>

        {/* Modal to view Profile */}
        { isModalOpen &&
            <ProfileModal
                modalProfile={modalProfile}
                handleModalClose={handleModalClose}
            />
        }

        </div>
    );
}

export default MyLinks;
